import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  Image,
  Link,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetNewsMediaPublicationQuery } from "../../features/cmsApi";
import BreadcrumbComponent from "./BreadcrumbComponent";

interface NewsItem {
  id: string | number;
  title: string;
  content:
    | Array<{
        type: string;
        children: Array<{
          text: string;
          type: string;
        }>;
      }>
    | string;
  category: string | null;
  image?: string;
  thumbnail?: {
    url: string;
    formats?: {
      thumbnail?: {
        url: string;
      };
      small?: {
        url: string;
      };
      medium?: {
        url: string;
      };
    };
  };
  publishedDate: string;
  attachment?: string;
  link?: string | null;
  status?: string;
}

function truncate(text: string, max: number) {
  return text.length > max ? text.slice(0, max) + "…" : text;
}

function extractTextFromContent(content: NewsItem["content"]): string {
  if (typeof content === "string") {
    return content;
  }

  return content
    .map((paragraph) => paragraph.children.map((child) => child.text).join(""))
    .join(" ")
    .trim();
}

function News() {
  const { data } = useGetNewsMediaPublicationQuery();
  const [news, setNews] = useState<NewsItem[]>([]);
  const navigate = useNavigate();



  // Update news state when data is received
  useEffect(() => {
    if (data?.data?.news) {
      setNews(data.data.news);
    }
  }, [data]);

  const handleReadMore = (item: NewsItem) => {
    navigate(`/news/${item.id}`);
  };

  return (
    <Container maxW="7xl" py={12}>
      {/* Header Section */}
      <Box textAlign="center">
        <VStack align="center">
          <Flex justify="space-between" align="center" w="full">
            <BreadcrumbComponent
              data={[
                { title: "Home", href: "/" },
                { title: "News", href: null },
              ]}
            />
          </Flex>
          <Box textAlign="center" mb={{ base: 4, md: 4 }}>
            <Text
              fontSize={{ base: "2xl", sm: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="bold"
              color="#090047"
              fontFamily="Cinema Sunday"
            >
              News
            </Text>
          </Box>
        </VStack>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={{ base: 6, md: 8 }}>
        {news.map((item) => (
          <Card.Root
            key={item.id}
            overflow="hidden"
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-8px)",
              shadow: "xl",
              borderColor: "blue.200",
            }}
            bg="white"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="xl"
          >
            <Card.Header p={0}>
              {item.thumbnail?.url ? (
                <Avatar.Root 
                  shape="square" 
                  w="100%"
                  h={{ base: "180px", md: "200px", lg: "220px" }}
                  borderTopRadius="xl"
                  bg="gray.50"
                >
                  <Avatar.Fallback name={item.title} bg="gray.100" color="gray.500" />
                  <Avatar.Image 
                    src={`${import.meta.env.VITE_CMS_API_URL}${item?.thumbnail?.url}`}
                    objectFit="contain"
                    w="100%"
                    h="100%"
                  />
                </Avatar.Root>
              ) : (
                <Avatar.Root 
                  shape="square" 
                  w="100%"
                  h={{ base: "180px", md: "200px", lg: "220px" }}
                  bg="gray.100"
                  borderTopRadius="xl"
                >
                  <Avatar.Fallback name={item.title} bg="gray.100" color="gray.400" w="100%" h="100%">
                    <Text fontSize="4xl">📰</Text>
                  </Avatar.Fallback>
                </Avatar.Root>
              )}
            </Card.Header>

            <Card.Body p={{ base: 4, md: 6 }}>
              <Box>
                <Flex 
                  justify="space-between" 
                  align={{ base: "flex-start", sm: "center" }}
                  direction={{ base: "column", sm: "row" }}
                  gap={{ base: 2, sm: 0 }}
                  mb={4}
                >
                  <Badge
                    colorScheme={item.category ? "blue" : "gray"}
                    variant="subtle"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="semibold"
                  >
                    {item.category || "General"}
                  </Badge>
                  <Flex align="center" gap={1} color="gray.500" fontSize="sm">
                    <Text>📅</Text>
                    <Text>
                      {new Date(item.publishedDate).toLocaleDateString()}
                    </Text>
                  </Flex>
                </Flex>

                <Heading 
                  size={{ base: "sm", md: "md" }} 
                  lineHeight="1.3" 
                  mb={3} 
                  color="gray.800"
                >
                  {item.title}
                </Heading>

                <Text
                  color="gray.600"
                  fontSize={{ base: "xs", md: "sm" }}
                  lineHeight="1.6"
                  mb={4}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  display="-webkit-box"
                  css={{
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {truncate(extractTextFromContent(item.content), 120)}
                </Text>
              </Box>
            </Card.Body>

            <Card.Footer px={{ base: 4, md: 6 }} pb={{ base: 4, md: 6 }}>
              <Flex gap={3} w="100%" direction={{ base: "column", sm: "row" }}>
                <Button
                  size={{ base: "sm", md: "md" }}
                  variant="solid"
                  borderRadius="full"
                  bg={"#293354"}
                  _hover={{ bg: "#5E698D" }}
                  flex={1}
                  onClick={() => handleReadMore(item)}
                >
                  Read More
                </Button>

                {item.link && (
                  <Link
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    w={{ base: "100%", sm: "auto" }}
                  >
                    <Button
                      size={{ base: "sm", md: "md" }}
                      variant="outline"
                      colorScheme="teal"
                      borderRadius="full"
                      w={{ base: "100%", sm: "auto" }}
                    >
                      🔗 External Link
                    </Button>
                  </Link>
                )}
              </Flex>
            </Card.Footer>
          </Card.Root>
        ))}
      </SimpleGrid>

      {news.length === 0 && (
        <Box py={16} textAlign="center">
          <Text fontSize="6xl" opacity={0.3} mb={6}>
            📰
          </Text>
          <Heading size="lg" color="gray.600" mb={4}>
            No News Available
          </Heading>
          <Text color="gray.500">Check back later for the latest updates!</Text>
        </Box>
      )}
    </Container>
  );
}

export default News;
